import { reactive } from "vue";
import { trackAppSetting } from "@/analytics/app-analytics";
import { THEME_LIST, type Theme } from "@/components/layouts/navbar/Themes";
import $ls, { LocalStorageKey } from "@/utils/local-storage";

export const LEGEND_LIST_ID = "LegendList";

type ThemeName = "light" | "dark";

export type ListDisplayMode = "grid" | "list";
export type ItemDisplayMode = "card" | "line";

/**
 * Tailles de l'interface de l'outil dessin, réglées à part pour les icônes
 * (barres d'outils, onglets) et pour les textes (en-tête, volets). « lg » est
 * la taille historique, donc la valeur par défaut.
 */
export type CanvasUiSize = "xs" | "sm" | "md" | "lg" | "xl";

export const CANVAS_UI_SIZES: CanvasUiSize[] = ["xs", "sm", "md", "lg", "xl"];

export const DEFAULT_CANVAS_UI_SIZE: CanvasUiSize = "lg";

/** Facteur de zoom de chaque taille (cf. useCanvasUiScale). */
export const CANVAS_UI_SCALES: Record<CanvasUiSize, number> = {
  xs: 0.625,
  sm: 0.75,
  md: 0.875,
  lg: 1,
  xl: 1.25,
};

type Settings = {
  actviveTheme: Theme;
  onlyStylusOn: boolean;
  fastModeOn: boolean;
  arrowShape: "large_arrow" | "thin_arrow"; // no circle
  isHatchActive: boolean;
  displayMode: ListDisplayMode;
  autoSaveOn: boolean;
  /** Contextual one-line hints at the bottom of the drawing tool. */
  toolHintsOn: boolean;
  /**
   * Panneau de l'outil texte réduit à son seul champ, en bas à gauche du
   * canvas. Par appareil : c'est une affaire de taille d'écran.
   */
  textPanelCollapsed: boolean;
  /** Taille des icônes de l'outil dessin : barre d'outils, onglets, barres flottantes. */
  canvasIconSize: CanvasUiSize;
  /** Taille des textes de l'outil dessin : en-tête, volets, bandeaux. */
  canvasTextSize: CanvasUiSize;
  setActiveTheme: (name: ThemeName) => boolean;
  setStylusMode: (onlyStylusOn: boolean) => void;
  setFastMode: (fastModeOn: boolean) => void;
  setAutoSaveMode: (autoSaveOn: boolean) => void;
  toggleToolHints: () => void;
  setTextPanelCollapsed: (collapsed: boolean) => void;
  setCanvasIconSize: (size: CanvasUiSize) => void;
  setCanvasTextSize: (size: CanvasUiSize) => void;
  setPhotoMarkerShape: (shape: "large_arrow" | "thin_arrow") => void;
  setDisplayMode: (mode: ListDisplayMode) => void;
  activateHatch: (active: boolean) => void;
  initialize: () => void;
  persist: () => void;
};

export default reactive<Settings>({
  actviveTheme: THEME_LIST[0],
  fastModeOn: true,
  onlyStylusOn: true,
  arrowShape: "thin_arrow",
  isHatchActive: true,
  displayMode: "list",
  autoSaveOn: true,
  toolHintsOn: true,
  textPanelCollapsed: false,
  canvasIconSize: DEFAULT_CANVAS_UI_SIZE,
  canvasTextSize: DEFAULT_CANVAS_UI_SIZE,
  setActiveTheme(name: ThemeName) {
    const theme = THEME_LIST.find((theme) => theme.name === name);
    if (theme) {
      trackAppSetting("theme", name);
      this.actviveTheme = theme;
      this.persist();
      return true;
    } else {
      return false;
    }
  },
  setPhotoMarkerShape(shape) {
    // Not tracked: no settings-panel control writes this anymore — it is only
    // kept in sync by the canvas marker tool, whose own
    // photo_marker_shape_changed event already covers the gesture.
    this.arrowShape = shape;
    this.persist();
  },
  setStylusMode(onlyStylusOn: boolean) {
    trackAppSetting("stylus_only", onlyStylusOn);
    this.onlyStylusOn = onlyStylusOn;
    this.persist();
  },
  setFastMode(fastModeOn: boolean) {
    trackAppSetting("fast_mode", fastModeOn);
    this.fastModeOn = fastModeOn;
    this.persist();
  },
  setAutoSaveMode(autoSaveOn: boolean) {
    trackAppSetting("auto_save", autoSaveOn);
    this.autoSaveOn = autoSaveOn;
    this.persist();
  },
  toggleToolHints() {
    this.toolHintsOn = !this.toolHintsOn;
    trackAppSetting("tool_hints", this.toolHintsOn);
    this.persist();
  },
  // Not tracked: a layout convenience, not a drawing setting.
  setTextPanelCollapsed(collapsed: boolean) {
    this.textPanelCollapsed = collapsed;
    this.persist();
  },
  setCanvasIconSize(size: CanvasUiSize) {
    if (!CANVAS_UI_SIZES.includes(size)) return;
    trackAppSetting("canvas_icon_size", size);
    this.canvasIconSize = size;
    this.persist();
  },
  setCanvasTextSize(size: CanvasUiSize) {
    if (!CANVAS_UI_SIZES.includes(size)) return;
    trackAppSetting("canvas_text_size", size);
    this.canvasTextSize = size;
    this.persist();
  },
  activateHatch(active: boolean) {
    trackAppSetting("hatch", active);
    this.isHatchActive = active;
    this.persist();
  },
  setDisplayMode(mode: ListDisplayMode) {
    trackAppSetting("list_display_mode", mode);
    this.displayMode = mode;
    this.persist();
  },
  initialize() {
    const storedSettings = $ls.getItem(LocalStorageKey.SETTINGS);
    if (storedSettings) {
      const settings = JSON.parse(storedSettings) as Settings;
      if (settings.actviveTheme) this.actviveTheme = settings.actviveTheme;
      if (settings.onlyStylusOn != undefined)
        this.onlyStylusOn = settings.onlyStylusOn;
      if (settings.fastModeOn != undefined)
        this.fastModeOn = settings.fastModeOn;
      if (settings.arrowShape) this.arrowShape = settings.arrowShape;
      if (settings.isHatchActive != undefined)
        this.isHatchActive = settings.isHatchActive;
      if (settings.displayMode != undefined)
        this.displayMode = settings.displayMode;
      if (settings.autoSaveOn != undefined)
        this.autoSaveOn = settings.autoSaveOn;
      if (settings.toolHintsOn != undefined)
        this.toolHintsOn = settings.toolHintsOn;
      if (settings.textPanelCollapsed != undefined)
        this.textPanelCollapsed = settings.textPanelCollapsed;
      // Avant la séparation icônes / textes, une seule taille : elle vaut
      // pour les deux tant qu'aucune n'a été réglée.
      const legacySize = (settings as { canvasUiSize?: CanvasUiSize })
        .canvasUiSize;
      const validSize = (size: CanvasUiSize | undefined) =>
        size !== undefined && CANVAS_UI_SIZES.includes(size) ? size : null;
      this.canvasIconSize =
        validSize(settings.canvasIconSize) ??
        validSize(legacySize) ??
        this.canvasIconSize;
      this.canvasTextSize =
        validSize(settings.canvasTextSize) ??
        validSize(legacySize) ??
        this.canvasTextSize;
    }
  },
  persist() {
    $ls.setItem(LocalStorageKey.SETTINGS, JSON.stringify(this));
  },
});
